import { useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import Colors from 'colors.json'

import Button from 'components/Button'
import Dropdown from 'components/Dropdown'
import Spinner from 'components/Spinner'
import { useFileTypes } from './useFileTypes'
import { useFiles } from './useFiles'
import { postDownloadFile } from 'api/files'
import { useFilesStore } from 'store/files'

type FormValues = {
  fileTypeId: number | null
  fileName: string | null
}

interface Props {
  onCloseModal: () => void
}

const FilesDownloadForm = ({ onCloseModal }: Props): JSX.Element => {

  const [formValues, setFormValues] = useState<FormValues>({
    fileTypeId: null,
    fileName: null,
  })

  const { fileTypesOptions, fileTypesModStatus } = useFileTypes()
  const { filesOptions, filesModStatus, files } = useFiles(formValues.fileTypeId ?? undefined)

  const fileDownloadModStatusSet = useFilesStore(
    (state) => state.fileDownloadModStatusSet
  )
  const selectedFileData = files.find((f) => f.fileName === formValues.fileName)
  const isTypeLoading = fileTypesModStatus !== 'success'
  const isFilesLoading = formValues.fileTypeId && filesModStatus !== 'success'

  const handleFileTypeSelect = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => {
      setFormValues({
        fileTypeId: parseInt(data.value),
        fileName: null, // Reset file selection when file type changes
      })
    },
    []
  )
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFileSelect = useCallback((data: any) => {
    setFormValues((prev) => ({
      ...prev,
      fileName: data.value,
    }))
  }, [])

  const handleDownload = useCallback(async () => {
    if (!selectedFileData) {
      toast.error('Please select a file to download')
      return
    }

    try {
      fileDownloadModStatusSet('loading')
      await postDownloadFile({
        fileName: selectedFileData.fileName,
      })
      toast.success('File downloaded successfully!')
      fileDownloadModStatusSet('success')
    } catch (error) {
      fileDownloadModStatusSet('fail')
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('Failed to download file')
      }
    }
  }, [selectedFileData, fileDownloadModStatusSet])

  if (isTypeLoading) {
    return <Spinner />
  }

  const selectedFileTypeOption = fileTypesOptions.find(
    (opt) => parseInt(opt.value) === formValues.fileTypeId
  )

  const selectedFileOption = filesOptions.find(
    (opt) => opt.value === formValues.fileName
  )
console.log("selectedFileOption", selectedFileOption)
  return (
    <div className="flex flex-col gap-6">
      {/* File Type Selection */}
      <div>
        <Dropdown
          title="File Type"
          options={fileTypesOptions}
          className="w-full tracking-wider"
          borderColor="#C4C4C4"
          onSelect={handleFileTypeSelect}
          value={selectedFileTypeOption || null}
          menuMaxHeight={250}
          menuPortalTarget={document.body}
        />
      </div>

      {/* File Selection */}
      {formValues.fileTypeId && (
        <div>
          {isFilesLoading ? (
            <Spinner />
          ) : (
            <>
              <Dropdown
                title="File"
                options={filesOptions}
                className="w-full tracking-wider"
                borderColor="#C4C4C4"
                onSelect={handleFileSelect}
                disabled={filesOptions.length === 0}
                value={selectedFileOption || null}
                menuMaxHeight={250}
                menuPortalTarget={document.body}
              />

              {/* File Details */}
              {selectedFileData && (
                <div className="mt-4 p-4 bg-gray-50 rounded border border-gray-200">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">File Name</p>
                      <p className="font-medium">{selectedFileData.fileName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Record Count</p>
                      <p className="font-medium">{selectedFileData.recordCount.toLocaleString()}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 uppercase">Creation Date</p>
                      <p className="font-medium">{new Date(selectedFileData.creationDate).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}

              {filesOptions.length === 0 && (
                <p className="mt-4 text-sm text-gray-500">No files available for this type</p>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button
          type="button"
          className="rounded bg-primary text-white"
          onClick={handleDownload}
          disabled={!selectedFileData || !!isFilesLoading}
        >
          Download
        </Button>
        <Button
          className="bg-white border rounded border-primary"
          onClick={onCloseModal}
          style={{ color: Colors.primary }}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}

export default FilesDownloadForm
